import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';

import styled from 'styled-components';

import Cart from '../components/Cart';
import ResourceTracker from '../components/ResourceTracker';

import { corporationMap } from '../game/corporations';
import gameFuncs from '../context/gameStateFunctional';

import cardBGSmall from '../assets/TerraformingResourceCard-1280.webp';
import cardBGLarge from '../assets/TerraformingResourceCard-2560.webp';
import useWindowDimensions from '../utils/windowSize';
import { calculateBoardSize } from '../utils/functions';
import { loadStateFromStorage, saveStateToStorage } from '../utils/storage';

import { Button } from '@mui/material';
import { steps } from '../utils/tutorial';

const getNewGameState = (corporation) => gameFuncs.createGameState(corporation);

const PlayerResources = () => {
  // window size
  const { height, width } = useWindowDimensions();

  const { boardHeight, boardWidth, blockSize } = calculateBoardSize(
    height,
    width
  );

  // navigation
  const { corp } = useParams();
  const corporation = corporationMap[corp];

  // state
  const [cartActive, setCartActive] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [history, setHistory] = useState([getNewGameState(corporation)]);
  const [future, setFuture] = useState([]);
  // Joyride states
  const [showTutorial, setShowTutorial] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // const [description, effect] = corporation?.text?.split(
  //   /\s\|\sEffect:\s*|\s\|\sAction:\s*/g
  // );

  const setGameState = (newGameState) => {
    const newHistory = [...history, newGameState];
    setHistory(newHistory);
    setFuture([]);

    saveStateToStorage(corp, newHistory, []);
  };

  const undo = () => {
    if (history.length < 2) return;
    const newFuture = [...future, history.at(-1)];
    const newHistory = history.slice(0, -1);
    setFuture(newFuture);
    setHistory(newHistory);

    saveStateToStorage(corp, newHistory, newFuture);
  };

  const redo = () => {
    if (future.length < 1) return;
    const newHistory = [...history, future.at(-1)];
    const newFuture = future.slice(0, -1);
    setFuture(newFuture);
    setHistory(newHistory);

    saveStateToStorage(corp, newHistory, newFuture);
  };

  const resourcePhase = () => {
    const newGameState = gameFuncs.resourcePhase(gameState);
    setGameState(newGameState);
  };

  const adjustResource = (delta) => {
    if (selectedResource.includes('Production')) {
      return setGameState(
        gameFuncs.adjustProduction(
          gameState,
          selectedResource.replace('Production', ''),
          delta
        )
      );
    }

    return setGameState(
      gameFuncs.adjustResource(gameState, selectedResource, delta)
    );
  };

  const adjustTerraformRating = (delta) => {
    setGameState(gameFuncs.adjustTerraformRating(gameState, delta));
  };

  const toggleCart = () => {
    setCartActive(!cartActive);
  };

  const resetGameState = () => {
    const newHistory = [getNewGameState(corporation)];
    setHistory(newHistory);
    setFuture([]);

    saveStateToStorage(corp, newHistory, []);
  };

  // TODO: cart functions
  const controlButtons = [-10, -5, -1, 1, 5, 10].map((delta) => (
    <Button
      key={delta}
      variant="contained"
      color={delta < 0 ? 'error' : 'success'}
      onClick={() => adjustResource(delta)}
      sx={{ fontSize: '1.9rem', fontWeight: 'bold' }}
    >
      {delta > 0 ? '+' : ''}
      {delta}
    </Button>
  ));

  useEffect(() => {
    const savedState = loadStateFromStorage(corp);

    if (!savedState) return;

    const { history, future } = savedState;

    setHistory(history);
    setFuture(future);
  }, [corp]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
    handleClose();
  };

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      // Update state to advance the tour
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Need to set our running state to false, so we can restart if we click start again.
      setShowTutorial(false);
      setStepIndex(0);
    }
  };

  const executePurchase = (gameState, deductions) => {
    setGameState(gameFuncs.purchase(gameState, deductions));
  };

  const toggleTutorial = () => {
    setShowTutorial(!showTutorial);
    handleClose();
  };

  const gameHeaderProps = {
    corporation,
    toggleFullScreen,
    showTutorial,
    toggleTutorial,
    resetGameState,
    handleMenuClick,
    open,
    anchorEl,
    handleClose,
  };

  const gameState = history.at(-1);

  return (
    <Wrapper id="resource-wrapper">
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        run={showTutorial}
        showProgress
        stepIndex={stepIndex}
        steps={steps}
      />

      <MainContent className={width > height ? 'landscape' : 'portrait'}>
        <ResourceBoard
          id="ResourceBoard"
          boardHeight={boardHeight}
          boardWidth={boardWidth}
        >
          <div className="row top">
            <ResourceTracker
              idBase="Credit"
              type="money"
              resourceData={gameState?.money}
              blockSize={blockSize}
              selectResource={setSelectedResource}
              selectedResource={selectedResource}
              minMod={-5}
            />
            <ResourceTracker
              idBase="Steel"
              type="steel"
              resourceData={gameState?.steel}
              blockSize={blockSize}
              selectResource={setSelectedResource}
              selectedResource={selectedResource}
            />
            <ResourceTracker
              idBase="Titanium"
              type="titanium"
              resourceData={gameState?.titanium}
              blockSize={blockSize}
              selectResource={setSelectedResource}
              selectedResource={selectedResource}
            />
          </div>
          <div className="row">
            <ResourceTracker
              idBase="Plants"
              type="plants"
              resourceData={gameState?.plants}
              blockSize={blockSize}
              selectResource={setSelectedResource}
              selectedResource={selectedResource}
            />
            <ResourceTracker
              idBase="Energy"
              type="energy"
              resourceData={gameState?.energy}
              blockSize={blockSize}
              selectResource={setSelectedResource}
              selectedResource={selectedResource}
            />
            <ResourceTracker
              idBase="Heat"
              type="heat"
              resourceData={gameState?.heat}
              blockSize={blockSize}
              selectResource={setSelectedResource}
              selectedResource={selectedResource}
            />
          </div>
          {selectedResource || showTutorial ? (
            <ControlBar id="ControlBar">{controlButtons}</ControlBar>
          ) : null}
        </ResourceBoard>
        <Cart
          adjustTerraformRating={adjustTerraformRating}
          cartActive={cartActive}
          corporation={corporation}
          executePurchase={executePurchase}
          future={future}
          gameHeaderProps={gameHeaderProps}
          gameState={gameState}
          history={history}
          redo={redo}
          resourcePhase={resourcePhase}
          showTutorial={showTutorial}
          toggleCart={toggleCart}
          tutorialStepIndex={stepIndex}
          undo={undo}
        />
      </MainContent>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.div`
  height: calc(100vh - 50px);
  display: grid;
  grid-template-columns: auto 1fr;
  &.landscape {
    grid-template-columns: auto 1fr;
  }
  &.portrait {
    grid-template-columns: auto;
    grid-template-rows: auto 1fr;
  }
`;

// BG 5129 × 3736
const ResourceBoard = styled.div`
  position: relative;
  background-image: url(${({ boardWidth }) =>
    boardWidth * (window.devicePixelRatio || 1) > 1280 ? cardBGLarge : cardBGSmall});
  background-size: contain;
  background-repeat: no-repeat;
  padding: 2.7% 2.6% 4.3% 2.6%;

  height: ${({ boardHeight }) => boardHeight}px;
  width: ${({ boardWidth }) => boardWidth}px;

  .row {
    display: grid;
    grid-template-columns: 35% 30% 33%;
    gap: 1%;
    height: 45.5%;
  }

  .top {
    height: 55.5%;
    margin-bottom: 1.8%;
    grid-template-columns: 41% 28.6% 28.4%;
  }
`;

const ControlBar = styled.div`
  position: absolute;
  top: 48.75%;
  right: 0;
  height: 6.85%;
  width: 58%;
  padding: 0.4%;
  padding-left: 1.5%;
  padding-right: 4.3%;
  display: flex;
  justify-content: space-between;
`;

export default PlayerResources;
